// The board's drag re-plan and pins (spec 03 §3.6, §4.6, P3-I-3, D-403): the
// flow from a drop or the drawer's move form to the server's preview, the
// consequences panel with Confirm and Cancel, the move form that is the drag's
// keyboard and tap alternative, and the Pins panel with Unpin. The preview
// saves nothing; Confirm commits the previewed move and publishes it, and a
// commit the factory overtook answers with a fresh preview that is drawn in
// its place.

import { useCallback, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { problemExtra } from "../hooks/planning-queries";
import {
  commitScheduleMove,
  previewScheduleMove,
  removeScheduleDirective,
  useScheduleDirectives,
  type ScheduleDirective,
  type ScheduleHeader,
  type ScheduleLane,
  type ScheduleLateOrderChange,
  type ScheduleMovePreview,
  type ScheduleMoveRequest,
  type ScheduleOperation,
} from "../hooks/schedule-queries";
import { formatDate, useOffline, weekLabel } from "../lib/planning-lib";
import { laneName, zonedStamp } from "../lib/schedule-lib";
import {
  kpiRows,
  moveBlock,
  signed,
  startIso,
  zonedInputMs,
  zonedInputValue,
} from "../lib/schedule-moves-lib";

/** A move the planner asked for: by a drop or by the move form. */
export interface MoveRequest {
  readonly op: ScheduleOperation;
  readonly startMs: number;
  readonly workCenterId: string;
}

/** What went wrong with a preview or a commit: the problem code and the request id. */
export interface MoveProblem {
  readonly code: string | undefined;
  readonly requestId: string | undefined;
}

export type MoveState =
  | { readonly phase: "previewing"; readonly req: MoveRequest }
  | {
      readonly phase: "preview" | "committing";
      readonly req: MoveRequest;
      readonly preview: ScheduleMovePreview;
      /** The commit answered 409 `PREVIEW_STALE` and this is the fresh preview. */
      readonly stale: boolean;
      readonly problem?: MoveProblem | undefined;
    }
  | {
      readonly phase: "refused";
      readonly req: MoveRequest;
      readonly problem: MoveProblem;
    };

/** The 422s and 404s a preview or a commit refuses with, each with its own line. */
export const MOVE_REFUSALS = [
  "OP_IN_PROGRESS",
  "OP_NOT_MOVABLE",
  "MOVE_IN_PAST",
  "WORK_CENTER_NOT_ALLOWED",
  "PIN_OVERLAPS_FIXED_WORK",
  "SCHEDULE_OPERATION_NOT_FOUND",
  "MOVE_BREAKS_PRECEDENCE",
  "SCHEDULE_RUN_NOT_FOUND",
] as const;

/** The body of rows 68 and 69: the lane is named only when it changes. */
export function moveBody(req: MoveRequest): ScheduleMoveRequest {
  return {
    op_ref: req.op.op_ref,
    start: startIso(req.startMs),
    ...(req.workCenterId !== req.op.work_center_id ? { work_center_id: req.workCenterId } : {}),
  };
}

function problemOf(error: unknown): MoveProblem {
  return {
    code: error instanceof RequestError ? error.code : undefined,
    requestId: requestIdOf(error),
  };
}

function isPreview(value: unknown): value is ScheduleMovePreview {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { preview_run_id?: unknown }).preview_run_id === "string" &&
    Array.isArray((value as { moved_ops?: unknown }).moved_ops)
  );
}

/**
 * The preview and commit of one move against the published schedule
 * (§3.6 steps 1 to 4). A preview answered 409 `SCHEDULE_SUPERSEDED` re-reads
 * the board and says so; a commit answered 409 `PREVIEW_STALE` draws the fresh
 * preview it carries, which can itself be confirmed.
 */
export function useMoveFlow({
  scheduleId,
  refresh,
  onCommitted,
  onSuperseded,
}: {
  scheduleId: string | undefined;
  refresh: () => Promise<void>;
  onCommitted: (header: ScheduleHeader) => void;
  onSuperseded: () => void;
}) {
  const navigate = useNavigate();
  const [state, setState] = useState<MoveState | undefined>(undefined);

  const preview = useCallback(
    async (req: MoveRequest) => {
      if (scheduleId === undefined) return;
      setState({ phase: "previewing", req });
      try {
        const result = await previewScheduleMove(scheduleId, moveBody(req));
        setState({ phase: "preview", req, preview: result, stale: false });
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          void navigate({ to: "/login" });
          return;
        }
        const problem = problemOf(error);
        if (problem.code === "SCHEDULE_SUPERSEDED") {
          setState(undefined);
          await refresh();
          onSuperseded();
          return;
        }
        setState({ phase: "refused", req, problem });
      }
    },
    [scheduleId, navigate, refresh, onSuperseded],
  );

  const confirm = useCallback(async () => {
    if (state?.phase !== "preview") return;
    const { req, preview: shown, stale } = state;
    setState({ phase: "committing", req, preview: shown, stale });
    try {
      // The commit names the move as the preview it confirms does: its
      // schedule, its reference on that schedule, its start and the lane it
      // resolved, so a fresh preview from a 409 commits as it was drawn (D-418).
      const header = await commitScheduleMove(shown.schedule_id, {
        op_ref: shown.op_ref,
        start: shown.start,
        work_center_id: shown.work_center_id,
        preview_run_id: shown.preview_run_id,
      });
      setState(undefined);
      await refresh();
      onCommitted(header);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const problem = problemOf(error);
      const fresh = problemExtra(error, "preview");
      if (problem.code === "PREVIEW_STALE" && isPreview(fresh)) {
        setState({ phase: "preview", req, preview: fresh, stale: true });
        return;
      }
      if (problem.code === "SCHEDULE_SUPERSEDED") {
        setState(undefined);
        await refresh();
        onSuperseded();
        return;
      }
      setState({ phase: "preview", req, preview: shown, stale, problem });
    }
  }, [state, navigate, refresh, onCommitted, onSuperseded]);

  const cancel = useCallback(() => {
    setState(undefined);
  }, []);

  const again = useCallback(() => {
    if (state !== undefined) void preview(state.req);
  }, [state, preview]);

  return { state, preview, confirm, cancel, again };
}

// ---- the consequences panel ------------------------------------------------------------

/** Whether a problem code has its own line under `planning.schedule.move.refused`. */
function isRefusal(code: string | undefined): boolean {
  return code !== undefined && (MOVE_REFUSALS as readonly string[]).includes(code);
}

/**
 * What the move changes, before anything is saved (§3.6 step 3): the orders
 * that newly finish late in red with their slip days, those no longer late in
 * green, the KPI delta and the new overloads, with Confirm and Cancel. It sits
 * above the lanes, so the ghost blocks it explains stay in view.
 */
export function ConsequencesPanel({
  state,
  lanes,
  timezone,
  onConfirm,
  onCancel,
  onAgain,
}: {
  state: MoveState;
  lanes: readonly ScheduleLane[];
  timezone: string;
  onConfirm: () => void;
  onCancel: () => void;
  onAgain: () => void;
}) {
  const { t, i18n } = useTranslation();
  const offline = useOffline();
  const nameOf = (id: string) =>
    laneName(
      lanes.find((l) => l.work_center_id === id),
      id,
    );
  const { req } = state;
  const order = req.op.label.order_no ?? t("planning.schedule.block.plannedOrder");
  const heading = t("planning.schedule.move.heading", {
    order,
    step: req.op.step_name,
    time: zonedStamp(new Date(req.startMs).toISOString(), i18n.language, timezone),
    wc: nameOf(req.workCenterId),
  });
  const preview = state.phase === "preview" || state.phase === "committing" ? state.preview : null;
  const problem =
    state.phase === "refused"
      ? state.problem
      : state.phase === "preview"
        ? state.problem
        : undefined;
  const conflicts = preview?.precedence_conflicts ?? [];
  const firstConflict = conflicts[0];
  const canConfirm =
    state.phase === "preview" && !offline && conflicts.length === 0 && problem === undefined;
  return (
    <section
      className="planning-move-panel"
      role="region"
      aria-label={t("planning.schedule.move.title")}
      data-testid="planning-schedule-consequences"
      data-phase={state.phase}
      data-op-ref={req.op.op_ref}
      data-preview-run-id={preview?.preview_run_id ?? ""}
    >
      <header className="planning-move-panel-head">
        <h2 className="planning-subhead">{t("planning.schedule.move.title")}</h2>
        <p className="planning-hint" data-testid="planning-schedule-move-heading">
          {heading}
        </p>
      </header>
      {state.phase === "previewing" ? (
        <CardSkeleton lines={3} label={t("planning.schedule.move.previewing")} />
      ) : null}
      {(state.phase === "preview" || state.phase === "committing") && state.stale ? (
        <Banner kind="warn" data-testid="planning-schedule-move-stale">
          {t("planning.schedule.move.stale")}
        </Banner>
      ) : null}
      {problem !== undefined ? (
        <Banner
          kind="bad"
          data-testid="planning-schedule-move-problem"
          data-code={problem.code ?? ""}
        >
          {isRefusal(problem.code)
            ? t(`planning.schedule.move.refused.${problem.code ?? ""}`)
            : t("planning.schedule.move.failed", { requestId: problem.requestId ?? "-" })}
        </Banner>
      ) : null}
      {firstConflict !== undefined ? (
        <Banner kind="warn" data-testid="planning-schedule-move-precedence">
          {t("planning.schedule.move.precedence", {
            time: zonedStamp(firstConflict.predecessor_end, i18n.language, timezone),
          })}
        </Banner>
      ) : null}
      {preview !== null ? (
        <PreviewFacts preview={preview} nameOf={nameOf} timezone={timezone} />
      ) : null}
      <div className="planning-actions planning-move-panel-actions">
        <Button
          disabled={state.phase === "committing"}
          data-testid="planning-schedule-move-cancel"
          onClick={onCancel}
        >
          {t("planning.schedule.move.cancel")}
        </Button>
        {problem?.code === "SCHEDULE_RUN_NOT_FOUND" || state.phase === "refused" ? (
          <Button data-testid="planning-schedule-move-again" onClick={onAgain}>
            {t("planning.schedule.move.previewAgain")}
          </Button>
        ) : null}
        {state.phase !== "refused" ? (
          <Button
            variant="primary"
            busy={state.phase === "committing"}
            busyLabel={t("planning.schedule.move.confirming")}
            disabled={!canConfirm}
            data-testid="planning-schedule-move-confirm"
            onClick={onConfirm}
          >
            {t("planning.schedule.move.confirm")}
          </Button>
        ) : null}
      </div>
      {offline ? <p className="planning-note">{t("planning.common.offlineWrites")}</p> : null}
    </section>
  );
}

function PreviewFacts({
  preview,
  nameOf,
  timezone,
}: {
  preview: ScheduleMovePreview;
  nameOf: (id: string) => string;
  timezone: string;
}) {
  const { t, i18n } = useTranslation();
  // A pegged order names its customer as module 09 renders it for the reader
  // (M03-S11), so the planner sees whose promise a move breaks.
  const orderOf = (o: ScheduleLateOrderChange) => {
    const order = o.label.order_no ?? t("planning.schedule.block.plannedOrder");
    const customer = o.label.sales_order?.customer_name ?? null;
    return customer === null ? order : t("planning.schedule.move.orderFor", { order, customer });
  };
  const end = (iso: string | null) =>
    iso === null
      ? t("planning.schedule.move.unplacedEnd")
      : zonedStamp(iso, i18n.language, timezone);
  const late = preview.newly_late_orders;
  const resolved = preview.resolved_late_orders;
  return (
    <div className="planning-move-facts">
      <div>
        <h3>{t("planning.schedule.move.newlyLate", { count: preview.newly_late_count })}</h3>
        {late.length === 0 ? (
          <p className="planning-hint">{t("planning.schedule.move.noneLate")}</p>
        ) : (
          <ul className="planning-line-list">
            {late.map((o, i) => (
              <li
                key={o.order_ref ?? `late-${String(i)}`}
                className="planning-bad"
                data-testid="planning-schedule-move-late"
                data-order-ref={o.order_ref ?? ""}
                data-order-no={o.label.order_no ?? ""}
                data-customer={o.label.sales_order?.customer_name ?? ""}
                data-old-end={o.old_end ?? ""}
                data-new-end={o.new_end ?? ""}
                data-slip-days={o.slip_days}
                data-promise-backed={String(o.promise_backed)}
              >
                {t("planning.schedule.move.slip", {
                  order: orderOf(o),
                  due: formatDate(o.due_date, i18n.language),
                  end: end(o.new_end),
                  old: end(o.old_end),
                  count: o.slip_days,
                })}
                {/* D-470 ruling 3: a slip on an order a customer holds a date
                    for. The promise number is served to planning.read
                    holders only; others see the flag alone. */}
                {o.promise_backed ? (
                  <>
                    {" "}
                    <StatusChip
                      variant="warn"
                      dot={false}
                      data-testid="planning-schedule-move-promise"
                      data-promise-no={o.promise?.promise_no ?? ""}
                      data-promise-status={o.promise?.status ?? ""}
                    >
                      {o.promise != null
                        ? t("planning.schedule.move.promisedNo", { number: o.promise.promise_no })
                        : t("planning.schedule.move.promised")}
                    </StatusChip>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <MoreLine shown={late.length} total={preview.newly_late_count} />
      </div>
      <div>
        <h3>{t("planning.schedule.move.resolved", { count: preview.resolved_late_count })}</h3>
        {resolved.length === 0 ? (
          <p className="planning-hint">{t("planning.schedule.move.noneResolved")}</p>
        ) : (
          <ul className="planning-line-list">
            {resolved.map((o, i) => (
              <li
                key={o.order_ref ?? `resolved-${String(i)}`}
                className="planning-good"
                data-testid="planning-schedule-move-resolved"
                data-order-ref={o.order_ref ?? ""}
                data-order-no={o.label.order_no ?? ""}
              >
                {t("planning.schedule.move.gain", {
                  order: orderOf(o),
                  due: formatDate(o.due_date, i18n.language),
                  end: end(o.new_end),
                })}
              </li>
            ))}
          </ul>
        )}
        <MoreLine shown={resolved.length} total={preview.resolved_late_count} />
      </div>
      <div>
        <h3>{t("planning.schedule.move.kpis")}</h3>
        <ul className="planning-kpi-delta" data-testid="planning-schedule-move-kpis">
          {kpiRows(preview.kpi_delta).map((row) => (
            <li
              key={row.key}
              data-testid="planning-schedule-move-kpi"
              data-kpi={row.key}
              data-value={row.value}
              data-tone={row.tone}
              className={
                row.tone === "worse" ? "planning-bad" : row.tone === "better" ? "planning-good" : ""
              }
            >
              <span>{t(`planning.schedule.kpiDelta.${row.key}`)}</span>
              <strong>{signed(row.value, i18n.language)}</strong>
            </li>
          ))}
        </ul>
        <p
          className="planning-hint"
          data-testid="planning-schedule-move-moved"
          data-count={preview.moved_ops_count}
        >
          {t("planning.schedule.move.moved", { count: preview.moved_ops_count })}
        </p>
      </div>
      <div>
        <h3>{t("planning.schedule.move.newOverloads", { count: preview.new_overloads_count })}</h3>
        {preview.new_overloads.length === 0 ? (
          <p className="planning-hint">{t("planning.schedule.move.noneResolved")}</p>
        ) : (
          <ul className="planning-line-list">
            {preview.new_overloads.map((o) => (
              <li
                key={`${o.work_center_id}|${o.week_start}`}
                data-testid="planning-schedule-move-new-overload"
                data-week={o.week_start}
                data-hours={o.overload_hours}
              >
                <StatusChip variant="bad" dot={false}>
                  {t("planning.schedule.overload.marker", { hours: o.overload_hours })}
                </StatusChip>{" "}
                {nameOf(o.work_center_id)} · {weekLabel(o.week_start, i18n.language)}
              </li>
            ))}
          </ul>
        )}
        <MoreLine shown={preview.new_overloads.length} total={preview.new_overloads_count} />
      </div>
    </div>
  );
}

function MoreLine({ shown, total }: { shown: number; total: number }) {
  const { t } = useTranslation();
  if (total <= shown) return null;
  return (
    <p className="planning-hint">{t("planning.schedule.move.more", { count: total - shown })}</p>
  );
}

// ---- the move form (the drag's keyboard and tap alternative) ---------------------------

const HOUR_MS = 3_600_000;

/**
 * The block drawer's move form: a start in the facility's zone, an hour
 * earlier or later, and a lane of those the step may run on. Preview closes
 * the drawer and draws the same ghosts and consequences a drop does.
 */
export function MoveForm({
  op,
  lanes,
  timezone,
  onPreview,
}: {
  op: ScheduleOperation;
  lanes: readonly ScheduleLane[];
  timezone: string;
  onPreview: (req: MoveRequest) => void;
}) {
  const { t } = useTranslation();
  const offline = useOffline();
  const initialMs = op.planned_start !== null ? Date.parse(op.planned_start) : Date.now();
  const [start, setStart] = useState(() => zonedInputValue(initialMs, timezone));
  const [lane, setLane] = useState(op.work_center_id);
  const [error, setError] = useState<ReactNode | undefined>(undefined);
  const blocked = moveBlock(op);
  const allowed = op.allowed_work_center_ids.includes(op.work_center_id)
    ? op.allowed_work_center_ids
    : [op.work_center_id, ...op.allowed_work_center_ids];
  const step = (deltaMs: number) => {
    const ms = zonedInputMs(start, timezone);
    if (ms === undefined) return;
    setStart(zonedInputValue(ms + deltaMs, timezone));
    setError(undefined);
  };
  if (blocked !== undefined) {
    return (
      <section className="planning-drawer-section" data-testid="planning-schedule-move-form">
        <h3>{t("planning.schedule.move.formTitle")}</h3>
        <p
          className="planning-hint"
          data-testid="planning-schedule-move-blocked"
          data-reason={blocked}
        >
          {t(`planning.schedule.move.blocked.${blocked}`)}
        </p>
      </section>
    );
  }
  return (
    <section className="planning-drawer-section" data-testid="planning-schedule-move-form">
      <h3>{t("planning.schedule.move.formTitle")}</h3>
      {op.fixed === "pin_position" ? (
        <p className="planning-hint" data-testid="planning-schedule-move-pinned">
          {t("planning.schedule.move.pinned")}
        </p>
      ) : null}
      {op.pin_released ? (
        <p className="planning-warn" data-testid="planning-schedule-move-pin-released">
          {t("planning.schedule.move.pinReleased")}
        </p>
      ) : null}
      <form
        className="planning-move-form"
        onSubmit={(e) => {
          e.preventDefault();
          const ms = zonedInputMs(start, timezone);
          if (ms === undefined) {
            setError(t("planning.schedule.move.invalidStart"));
            return;
          }
          if (ms === initialMs && lane === op.work_center_id) {
            setError(t("planning.schedule.move.sameSpot"));
            return;
          }
          onPreview({ op, startMs: ms, workCenterId: lane });
        }}
      >
        <Field label={t("planning.schedule.move.start")}>
          <input
            type="datetime-local"
            value={start}
            step={60}
            data-testid="planning-schedule-move-start"
            onChange={(e) => {
              setStart(e.target.value);
              setError(undefined);
            }}
          />
        </Field>
        <div className="planning-move-steps">
          <Button
            size="sm"
            data-testid="planning-schedule-move-earlier"
            onClick={() => {
              step(-HOUR_MS);
            }}
          >
            {t("planning.schedule.move.earlier")}
          </Button>
          <Button
            size="sm"
            data-testid="planning-schedule-move-later"
            onClick={() => {
              step(HOUR_MS);
            }}
          >
            {t("planning.schedule.move.later")}
          </Button>
        </div>
        <Field label={t("planning.schedule.move.lane")}>
          <Select
            value={lane}
            data-testid="planning-schedule-move-lane"
            onChange={(e) => {
              setLane(e.target.value);
              setError(undefined);
            }}
          >
            {allowed.map((id) => (
              <option key={id} value={id}>
                {laneName(
                  lanes.find((l) => l.work_center_id === id),
                  id,
                )}
              </option>
            ))}
          </Select>
        </Field>
        {error !== undefined ? (
          <p className="planning-bad" role="alert" data-testid="planning-schedule-move-error">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          disabled={offline}
          data-testid="planning-schedule-move-preview"
        >
          {t("planning.schedule.move.preview")}
        </Button>
        {offline ? <p className="planning-note">{t("planning.common.offlineWrites")}</p> : null}
      </form>
    </section>
  );
}

// ---- the pins panel --------------------------------------------------------------------

/**
 * The facility's standing pins and defers (row 70) with Unpin (row 71) for a
 * holder of `planning.schedule.publish`. A removal arms the solve; the board
 * follows the next publish.
 */
export function PinsPanel({
  facilityId,
  lanes,
  timezone,
  canUnpin,
  onClose,
  onRefresh,
}: {
  facilityId: string;
  lanes: readonly ScheduleLane[];
  timezone: string;
  canUnpin: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const directives = useScheduleDirectives(facilityId);
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [line, setLine] = useState<{ kind: "ok" | "warn" | "bad"; text: string } | undefined>(
    undefined,
  );
  const nameOf = (id: string) =>
    laneName(
      lanes.find((l) => l.work_center_id === id),
      id,
    );

  const remove = async (d: ScheduleDirective) => {
    setBusy(d.id);
    setLine(undefined);
    try {
      await removeScheduleDirective(d.id);
      await Promise.all([directives.refetch(), onRefresh()]);
      setLine({ kind: "ok", text: t("planning.schedule.pins.removed") });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = error instanceof RequestError ? error.code : undefined;
      if (code === "SCHEDULE_DIRECTIVE_NOT_ACTIVE" || code === "SCHEDULE_DIRECTIVE_NOT_FOUND") {
        await directives.refetch();
        setLine({ kind: "warn", text: t("planning.schedule.pins.notActive") });
      } else {
        setLine({
          kind: "bad",
          text: t("planning.schedule.pins.failed", { requestId: requestIdOf(error) ?? "-" }),
        });
      }
    } finally {
      setBusy(undefined);
    }
  };

  const rows = directives.data ?? [];
  return (
    <Modal
      label={t("planning.schedule.pins.title")}
      heading={t("planning.schedule.pins.title")}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={busy !== undefined}
      className="planning-drawer"
      testId="planning-schedule-pins-panel"
    >
      <div data-count={rows.length}>
        {line !== undefined ? (
          <Banner kind={line.kind} data-testid="planning-schedule-pins-line">
            {line.text}
          </Banner>
        ) : null}
        {directives.isPending ? (
          <CardSkeleton lines={3} label={t("planning.schedule.pins.loading")} />
        ) : directives.error !== null ? (
          <ErrorState
            title={t("planning.schedule.pins.loadError")}
            retryLabel={t("planning.common.retry")}
            onRetry={() => void directives.refetch()}
            requestId={requestIdOf(directives.error)}
            requestIdLabel={t("planning.common.requestId")}
          />
        ) : rows.length === 0 ? (
          <div data-testid="planning-schedule-pins-empty">
            <EmptyState
              title={t("planning.schedule.pins.emptyTitle")}
              body={t("planning.schedule.pins.emptyBody")}
            />
          </div>
        ) : (
          <ul className="planning-pin-list">
            {rows.map((d) => (
              <li
                key={d.id}
                className="planning-pin"
                data-testid="planning-schedule-pin"
                data-directive-id={d.id}
                data-kind={d.kind}
                data-op-ref={d.op_ref ?? ""}
                data-order-ref={d.order_ref ?? ""}
                data-work-center-id={d.work_center_id ?? ""}
                data-pin-start={d.pin_start ?? ""}
              >
                <div className="planning-pin-head">
                  <StatusChip variant={d.kind === "defer" ? "warn" : "accent"} dot={false}>
                    {t(`planning.schedule.pins.kind.${d.kind}`)}
                  </StatusChip>
                  <strong>{d.label.order_no ?? t("planning.schedule.block.plannedOrder")}</strong>
                  {d.step_no !== null && d.step_name !== null ? (
                    <span>
                      {t("planning.schedule.pins.step", { no: d.step_no, name: d.step_name })}
                    </span>
                  ) : null}
                </div>
                <p className="planning-hint">
                  {d.work_center_id !== null
                    ? t("planning.schedule.pins.on", { wc: nameOf(d.work_center_id) })
                    : null}
                  {d.pin_start !== null ? (
                    <>
                      {" "}
                      {t("planning.schedule.pins.at", {
                        time: zonedStamp(d.pin_start, i18n.language, timezone),
                      })}
                    </>
                  ) : null}
                </p>
                <p className="planning-hint">
                  {t("planning.schedule.pins.since", {
                    time: zonedStamp(d.created_at, i18n.language, timezone),
                    origin: t(`planning.schedule.pins.origin.${d.origin}`),
                  })}
                </p>
                {canUnpin ? (
                  <Button
                    size="sm"
                    variant="danger"
                    busy={busy === d.id}
                    busyLabel={t("planning.schedule.pins.removing")}
                    disabled={offline || busy !== undefined}
                    data-testid="planning-schedule-unpin"
                    onClick={() => void remove(d)}
                  >
                    {d.kind === "defer"
                      ? t("planning.schedule.pins.undefer")
                      : t("planning.schedule.pins.unpin")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {!canUnpin && rows.length > 0 ? (
          <p className="planning-hint" data-testid="planning-schedule-pins-read-only">
            {t("planning.schedule.pins.readOnly")}
          </p>
        ) : null}
        {canUnpin && offline ? (
          <p className="planning-note">{t("planning.common.offlineWrites")}</p>
        ) : null}
      </div>
    </Modal>
  );
}
